import React from "react";
import SimpleBar from "simplebar-react";

/**
 * A scrolling area with the same scrollbar on every platform.
 *
 * The CSS-only version this replaces could not do the job. macOS hides overlay
 * scrollbars until you scroll and reserves no width for them; Windows draws a
 * wide opaque one that takes layout space; and Chrome ignores
 * `::-webkit-scrollbar` outright as soon as the standard `scrollbar-width` is
 * set, so the two syntaxes cannot even be combined. SimpleBar renders its own
 * bar as an element, which is the only way to get one appearance everywhere —
 * and the only way to get auto-hide, which no amount of styling provides.
 *
 * It does not touch scroll physics: the content still scrolls in a real
 * overflow container, so wheel, trackpad momentum, keyboard, touch and
 * scroll-anchoring all behave natively. Only the bar is drawn by the library.
 *
 * Styles come from _simplebar.scss, vendored into styles.scss — shared source
 *
 * Props:
 *  - className   applied to the scroll region, for the height constraint
 *  - autoHide    false keeps the bar visible; default is to fade it out
 *  - style       inline styles for the outer element
 */
export default function Scrollable({
  className,
  autoHide = true,
  style,
  children,
  ...rest
}) {
  return (
    <SimpleBar
      className={className ? `scrollable ${className}` : "scrollable"}
      autoHide={autoHide}
      style={style}
      // Keyboard users have to be able to reach and scroll the region, and a
      // plain div is not focusable. SimpleBar puts this on the element that
      // actually scrolls.
      scrollableNodeProps={{ tabIndex: 0 }}
      {...rest}
    >
      {children}
    </SimpleBar>
  );
}
